import React, { useEffect, useState } from 'react';
import { IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import { Link } from 'react-router-dom';
import { STATE_UI, socColor, fmtEta, fmtLastSeen, vpnDeepLink, ringColor, dominantState } from './chargeUi';
import GoalControlBar from './GoalControlBar';
import RollupChips from './RollupChips';
import ChargeMetrics from './ChargeMetrics';
import ChargeRing from './ChargeRing';
import Dialog from '../components/ui/Dialog';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import Panel from '../components/ui/Panel';
import useChargeStore from '../stores/useChargeStore';
import { useToast } from '../components/Toast/ToastContext';
import type { ChargerView, SlotView } from './types';

interface Props {
  charger: ChargerView;
  hasVpnAccess: boolean;
  onSetGoal: (percent: number) => Promise<boolean> | boolean | void;
  onClose: () => void;
}

function SlotCell({ slot, selected, onClick }: { slot: SlotView; selected: boolean; onClick: () => void }) {
  const ui = STATE_UI[slot.state];
  const extra = slot.fault ? ', fault' : slot.thermal ? ', thermal warning' : '';
  const socTxt = slot.soc != null ? `, ${Math.round(slot.soc)} percent` : '';
  return (
    <button
      type="button" onClick={onClick} aria-pressed={selected}
      aria-label={`Slot ${slot.location ?? '?'}, ${ui.label}${socTxt}${extra}`}
      className={`relative overflow-hidden rounded bg-[#3a3a3a] border flex items-center justify-center min-h-[28px] transition ${
        selected ? 'border-primary' : 'border-gray-700 hover:border-outline'
      } focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
    >
      <span className={`absolute inset-x-0 top-0 h-1 ${ui.dot}`} />
      {slot.state === 'charging' && <span className="cc-slot-charging" />}
      {slot.state === 'discharging' && <span className="cc-slot-discharging" />}
      <span className={`relative text-xs sm:text-sm font-semibold leading-none ${socColor(slot.soc)}`}>
        {slot.soc != null ? `${Math.round(slot.soc)}` : '·'}
      </span>
      {(slot.fault || slot.thermal) && (
        <span className={`absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${slot.fault ? 'bg-red-500' : 'bg-amber-400'}`} />
      )}
    </button>
  );
}

export default function ChargerDetail({ charger, hasVpnAccess, onSetGoal, onClose }: Props) {
  // Store the selected slot's stable id, derive the live slot each render so the
  // detail panel stays in sync with the 3s poll instead of freezing on a snapshot.
  const [selNodeId, setSelNodeId] = useState<string | number | null>(null);
  const sel = selNodeId != null ? charger.slots.find((s) => s.node_id === selNodeId) ?? null : null;
  const name = charger.display_code || charger.vpn_ip;
  const present = charger.rollup?.present_count ?? 0;
  const isCustomer = !charger.is_internal_pool;

  // Location: prefer live geo; fall back to the durable last-known location so an
  // offline charger still shows where it was, not a blank.
  const geoLat = charger.geo_lat ?? charger.last_seen_lat;
  const geoLng = charger.geo_lng ?? charger.last_seen_lng;
  const geoLabel = charger.geo_label ?? charger.last_seen_geo;
  const geoIsLive = charger.geo_label != null || charger.geo_lat != null;

  const { pinCharger, unpinCharger } = useChargeStore();
  const toast = useToast();
  // Optimistic local pin state (parent may not re-fetch this charger immediately).
  const [pinned, setPinned] = useState(!!charger.pinned);
  useEffect(() => { setPinned(!!charger.pinned); }, [charger.pinned]);

  const togglePin = async () => {
    if (!charger.asset_id) return;
    const next = !pinned;
    setPinned(next);
    try {
      if (next) { await pinCharger(charger.asset_id); toast.success('Pinned to My Chargers'); }
      else { await unpinCharger(charger.asset_id); toast.success('Removed from My Chargers'); }
    } catch (e: any) {
      setPinned(!next);
      toast.error(e?.message || 'Failed to update pin');
    }
  };

  return (
    <Dialog open onClose={onClose} label={name} size="lg">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <ChargeRing
            pct={charger.rollup?.soc_avg ?? null}
            color={charger.online ? ringColor(charger) : '#475569'}
            glow={charger.online && dominantState(charger) === 'charging'}
            size={46} bg="var(--surface-container-low)"
          />
          <div className="min-w-0">
            <div className="font-bold text-white truncate">{name}</div>
            <div className="text-xs text-on-surface-variant truncate">
              {charger.vpn_ip}
              {charger.online ? '' : ` · offline${fmtLastSeen(charger.last_seen) ? ` · seen ${fmtLastSeen(charger.last_seen)}` : ''}`}
              {charger.power_mode?.watts ? ` · ${charger.power_mode.watts}W` : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Deep link Control → Manage: jump to this charger's asset record. */}
          {charger.asset_id && (
            <Tooltip label="View asset record">
            <Link
              to={`/assets/${charger.asset_id}`}
              onClick={onClose}
              aria-label="View asset record"
              className="p-1.5 rounded transition text-on-surface-variant hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M16 13H8" />
                <path d="M16 17H8" />
                <path d="M10 9H8" />
              </svg>
            </Link>
            </Tooltip>
          )}
          <Tooltip label={pinned ? 'Pinned to My Chargers' : 'Pin to My Chargers'}>
          <button type="button" onClick={togglePin} disabled={!charger.asset_id}
            aria-pressed={pinned}
            aria-label={pinned ? 'Remove from My Chargers' : 'Pin to My Chargers'}
            className={`p-1.5 rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 ${
              pinned ? 'text-primary-ink bg-primary/10' : 'text-on-surface-variant hover:text-white'
            }`}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 17v5" />
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
            </svg>
          </button>
          </Tooltip>
          <Tooltip label="Close the charger"><IconButton label="Close the charger" onClick={onClose}><IconClose /></IconButton></Tooltip>
        </div>
      </div>

      <Reveal show={isCustomer}>
        <Alert tone="warning" className="mb-3">
          Heads up: this charger is assigned to a customer. It's view-only here, so charge goals are disabled.
        </Alert>
      </Reveal>

      {(geoLabel || geoLat != null) && (
        <div className="mb-3 text-sm flex items-center gap-2 flex-wrap">
          <span className="text-on-surface-variant">{geoIsLive ? 'Approx. location:' : 'Last seen:'}</span>
          <span className="text-on-surface min-w-0 break-words">
            {geoLabel ||
              (geoLat != null && geoLng != null
                ? `${geoLat.toFixed(2)}, ${geoLng.toFixed(2)}`
                : 'unknown')}
          </span>
          {geoLat != null && geoLng != null && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${geoLat},${geoLng}`}
              target="_blank" rel="noopener noreferrer"
              className="text-primary hover:underline"
            >map <span aria-hidden="true">↗</span></a>
          )}
        </div>
      )}

      <div className="mb-3">
        <ChargeMetrics goal={charger.goal_pct ?? null} etaHours={charger.rollup?.eta_hours_max ?? null} />
      </div>

      <div className="mb-4"><RollupChips rollup={charger.rollup} /></div>

      <div className="mb-4 pt-4 border-t border-gray-700/60">
        <GoalControlBar
          label="Set charger goal" currentGoal={charger.goal_pct}
          onSet={onSetGoal} disabled={!charger.online || isCustomer}
        />
      </div>

      {/* Open the charger's own Flask UI — only shown when the user has declared
          VPN access in Settings; otherwise nothing renders. */}
      {hasVpnAccess && (
        <a
          href={vpnDeepLink(charger.vpn_ip)} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mb-4 px-3 py-2 text-sm border border-outline-variant rounded text-on-surface hover:border-primary hover:text-primary transition"
        >Open with VPN <span aria-hidden="true">↗</span></a>
      )}

      <div className="text-xs text-on-surface-variant mb-2">
        Batteries ({present}/{charger.slot_count || 50})
      </div>
      {charger.slots.length ? (
        // Always 10 across — the grid mirrors the physical case layout
        // (1–10 top row … 41–50 bottom), so order by slot location, not poll order.
        <div className="grid grid-cols-10 gap-1 sm:gap-1.5">
          {[...charger.slots]
            .sort((a, b) => (a.location ?? 999) - (b.location ?? 999))
            .map((s) => (
              <SlotCell key={String(s.node_id)} slot={s}
                selected={selNodeId === s.node_id} onClick={() => setSelNodeId(s.node_id)} />
            ))}
        </div>
      ) : (
        <EmptyState variant="line">No live battery data.</EmptyState>
      )}

      <Reveal show={!!sel}>
      {sel && (
        <Panel on="panel" padding="sm" label={`Slot ${sel.location ?? 'unknown'}`} className="mt-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-white font-medium">Slot {sel.location ?? '—'}</span>
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${STATE_UI[sel.state].dot}`} />
              <span className={STATE_UI[sel.state].text}>{STATE_UI[sel.state].label}</span>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-on-surface-variant">
            <span>SOC <span className={socColor(sel.soc)}>{sel.soc != null ? `${Math.round(sel.soc)}%` : '—'}</span></span>
            <span>Temp {sel.temperature != null ? `${Math.round(sel.temperature)}°C` : '—'}</span>
            <span>Goal {sel.percent_goal != null ? `${sel.percent_goal}%` : '—'}</span>
            <span>ETA {fmtEta(sel.hours_to_goal_pct)}</span>
            {sel.model_name && <span className="col-span-2 truncate">{sel.model_name}</span>}
            {sel.cycles_no != null && <span>{sel.cycles_no} cycles</span>}
          </div>
          {sel.flags.length > 0 && (
            <div className="mt-1 text-xs text-on-surface-variant">flags: {sel.flags.join(', ')}</div>
          )}
        </Panel>
      )}
      </Reveal>
    </Dialog>
  );
}
